import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const features = [
  {
    icon: 'ri-battery-2-charge-line',
    label: 'Batería',
    helix: { value: '7 días', sub: 'con monitoreo 24/7', good: true },
    watch: { value: '1–2 días', sub: 'carga diaria obligatoria', good: false },
  },
  {
    icon: 'ri-scales-3-line',
    label: 'Peso',
    helix: { value: '< 5 g', sub: 'casi imperceptible', good: true },
    watch: { value: '30–50 g', sub: 'notable en la muñeca', good: false },
  },
  {
    icon: 'ri-heart-pulse-line',
    label: 'Precisión cardíaca',
    helix: { value: '99,2 %', sub: 'grado clínico validado', good: true },
    watch: { value: '~85 %', sub: 'variable según movimiento', good: false },
  },
  {
    icon: 'ri-moon-clear-line',
    label: 'Sueño con dispositivo',
    helix: { value: 'Cómodo', sub: 'olvidas que lo llevas', good: true },
    watch: { value: 'Incómodo', sub: 'muchos lo quitan por la noche', good: false },
  },
  {
    icon: 'ri-eye-off-line',
    label: 'Distracciones',
    helix: { value: 'Ninguna', sub: 'sin pantalla ni notificaciones', good: true },
    watch: { value: 'Constantes', sub: 'notificaciones todo el día', good: false },
  },
  {
    icon: 'ri-drop-line',
    label: 'Resistencia al agua',
    helix: { value: 'IP68 · 100m', sub: 'piscina, surf, ducha', good: true },
    watch: { value: 'IP67 · 1m', sub: 'salpicaduras básicas', good: false },
  },
  {
    icon: 'ri-gem-line',
    label: 'Estilo',
    helix: { value: 'Joya discreta', sub: 'pasa desapercibido', good: true },
    watch: { value: 'Pantalla visible', sub: 'look claramente tecnológico', good: false },
  },
  {
    icon: 'ri-price-tag-3-line',
    label: 'Precio',
    helix: { value: 'Desde 12€', sub: 'accesible y directo', good: true },
    watch: { value: 'Desde 199€', sub: 'hasta 800€ gama alta', good: false },
  },
];

const ComparisonSection = () => {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const { ref: tableRef, isVisible: tableVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: imgRef, isVisible: imgVisible } = useScrollAnimation({ threshold: 0.1 });

  const handleScrollToPricing = () => {
    document.querySelector('#precio')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="comparativa" className="py-28 w-full bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* ── Header ── */}
        <div
          ref={titleRef}
          className={`text-center max-w-2xl mx-auto mb-16 scroll-hidden ${titleVisible ? 'scroll-visible' : ''}`}
        >
          <span className="font-body text-xs font-semibold tracking-widest text-amber-700 uppercase block mb-4">
            Comparativa
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-light text-stone-900 leading-tight mb-5">
            El anillo que supera
            <br />
            <em className="not-italic font-semibold">al smartwatch.</em>
          </h2>
          <p className="font-body text-stone-500 text-base leading-relaxed">
            Más preciso, más cómodo y sin distracciones. Descubre por qué
            miles de personas han dejado el reloj inteligente por HELIX.
          </p>
        </div>

        {/* ── Product headers ── */}
        <div
          ref={imgRef}
          className={`grid grid-cols-2 gap-4 max-w-3xl mx-auto mb-2 scroll-hidden ${imgVisible ? 'scroll-visible' : ''}`}
        >
          {/* HELIX */}
          <div className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-stone-950 text-white">
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/8e87a908fa5058a29824832cadb754df.jpg"
                alt="HELIX Ring"
                className="w-full rounded-xl object-cover object-top"
                style={{ height: '110px' }}
              />
              <span className="absolute top-2 left-2 bg-amber-600 text-white font-body text-xs font-bold px-2.5 py-1 rounded-full">
                RECOMENDADO
              </span>
            </div>
            <div className="text-center">
              <div className="font-display text-lg font-semibold text-white">HELIX Ring</div>
              <div className="font-body text-xs text-white/50 mt-0.5">Anillo inteligente</div>
            </div>
          </div>

          {/* Smartwatch */}
          <div className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-stone-100 text-stone-600">
            <img
              src="https://readdy.ai/api/search-image?query=generic%20smartwatch%20on%20white%20background%2C%20product%20photography%2C%20clean%20studio%20shot%2C%20rectangular%20watch%20face%2C%20rubber%20band%2C%20minimalist%20background%2C%20no%20text%2C%20neutral%20tones&width=300&height=200&seq=compare-watch-01&orientation=landscape"
              alt="Smartwatch tradicional"
              className="w-full rounded-xl object-cover object-top"
              style={{ height: '110px' }}
            />
            <div className="text-center">
              <div className="font-display text-lg font-semibold text-stone-600">Smartwatch</div>
              <div className="font-body text-xs text-stone-400 mt-0.5">Reloj inteligente</div>
            </div>
          </div>
        </div>

        {/* ── Comparison table ── */}
        <div ref={tableRef} className="max-w-3xl mx-auto rounded-3xl overflow-hidden border border-stone-100">
          {features.map((row, i) => (
            <div
              key={row.label}
              className={`grid grid-cols-[1fr_1fr_1fr] border-b border-stone-100 last:border-0 scroll-hidden ${
                tableVisible ? 'scroll-visible' : ''
              } ${i % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}`}
              style={{ transitionDelay: tableVisible ? `${i * 0.06}s` : '0s' }}
            >
              {/* Feature label */}
              <div className="flex items-center gap-3 px-5 py-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-100 flex-shrink-0">
                  <i className={`${row.icon} text-stone-500 text-sm`} />
                </div>
                <span className="font-body text-sm font-medium text-stone-700 leading-tight">
                  {row.label}
                </span>
              </div>

              {/* HELIX column */}
              <div className="flex flex-col items-center justify-center px-4 py-4 bg-stone-950/[0.03] border-x border-stone-100 text-center">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-amber-600 text-sm" />
                  </div>
                  <span className="font-body text-sm font-semibold text-stone-900">
                    {row.helix.value}
                  </span>
                </div>
                <span className="font-body text-xs text-stone-400 leading-tight">
                  {row.helix.sub}
                </span>
              </div>

              {/* Smartwatch column */}
              <div className="flex flex-col items-center justify-center px-4 py-4 text-center">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${row.watch.good ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-close-circle-fill text-stone-300'} text-sm`} />
                  </div>
                  <span className="font-body text-sm font-medium text-stone-400">
                    {row.watch.value}
                  </span>
                </div>
                <span className="font-body text-xs text-stone-300 leading-tight">
                  {row.watch.sub}
                </span>
              </div>
            </div>
          ))}

          {/* Summary row */}
          <div className="grid grid-cols-[1fr_1fr_1fr] bg-stone-950">
            <div className="px-5 py-5 flex items-center">
              <span className="font-body text-xs font-semibold text-white/40 uppercase tracking-widest">
                Veredicto
              </span>
            </div>
            <div className="px-4 py-5 flex flex-col items-center justify-center text-center border-x border-white/10">
              <div className="flex items-center gap-1.5 mb-1">
                <i className="ri-trophy-fill text-amber-400 text-base" />
                <span className="font-display text-base font-semibold text-white">Ganador</span>
              </div>
              <span className="font-body text-xs text-white/40">8 de 8 categorías</span>
            </div>
            <div className="px-4 py-5 flex flex-col items-center justify-center text-center">
              <span className="font-display text-base font-semibold text-stone-500">Superado</span>
              <span className="font-body text-xs text-stone-600">0 de 8 categorías</span>
            </div>
          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <div
          className={`mt-12 text-center scroll-hidden ${titleVisible ? 'scroll-visible delay-500' : ''}`}
        >
          <p className="font-body text-stone-400 text-sm mb-5">
            ¿Convencido? Únete a más de 3 millones de personas que ya eligieron HELIX.
          </p>
          <button
            type="button"
            onClick={handleScrollToPricing}
            className="whitespace-nowrap inline-flex items-center gap-2 bg-stone-900 hover:bg-amber-700 text-white font-body font-medium text-sm tracking-wide px-8 py-4 rounded-full cursor-pointer transition-all duration-300 group"
          >
            Comprar mi HELIX Ring
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
          </button>
        </div>

      </div>
    </section>
  );
};

export default ComparisonSection;
